import { useEffect, useState } from "react";

import { LoadStatus, OverlayDimensions } from "../types";

/**
 * Программно загружает картинку по URL (через `new Image()`), отслеживает
 * status загрузки и natural-размеры. Не привязывается к DOM — компонент
 * может рендерить `<img>` отдельно и поделит ту же запись браузерного кеша.
 *
 * Cleanup отменяет применение результата к state, если URL сменился до
 * того как картинка догрузилась — иначе stale onload мог бы выставить
 * dims от прошлой картинки.
 */
export const useImageLoad = (url: string | undefined) => {
  const [status, setStatus] = useState<LoadStatus>(url ? "loading" : "idle");
  const [dims, setDims] = useState<OverlayDimensions | null>(null);

  /* eslint-disable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- асинхронная загрузка картинки через new Image(); сброс dims — часть load-жизненного цикла, а не derived state */
  useEffect(() => {
    if (!url) {
      setStatus("idle");
      setDims(null);
      return;
    }
    setStatus("loading");
    setDims(null);

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setDims({ w: img.naturalWidth, h: img.naturalHeight });
      setStatus("ok");
    };
    img.onerror = () => {
      if (cancelled) return;
      setStatus("error");
    };
    img.src = url;

    return () => {
      cancelled = true;
    };
  }, [url]);
  /* eslint-enable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */

  return { status, dims };
};
